import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import type {
  ServiceCategory,
  ServiceSubCategory,
  ServiceType,
  ServiceBehaviorType,
  CreateServiceCategoryRequest,
  UpdateServiceCategoryRequest,
  CreateServiceSubCategoryRequest,
  UpdateServiceSubCategoryRequest,
  CreateServiceTypeRequest,
  UpdateServiceTypeRequest
} from "@/types/serviceCategories";

// Service Categories Hooks
// API returns complete list - filtering/sorting done client-side
export function useServiceCategories(includeSubCategories = false, includeInactive = false) {
  return useQuery<ServiceCategory[]>({
    queryKey: ["/marketplace/service-categories", { includeSubCategories, includeInactive }],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (includeSubCategories) params.append('includeSubCategories', 'true');
      if (includeInactive) params.append('includeInactive', 'true');

      const queryString = params.toString();
      const url = queryString
        ? `/marketplace/service-categories?${queryString}`
        : `/marketplace/service-categories`;

      const response = await apiRequest("GET", url);
      return response.data || response;
    }
  });
}

export function useServiceCategory(categoryId: string | null, includeSubCategories = false) {
  return useQuery<ServiceCategory>({
    queryKey: [`/marketplace/service-categories/${categoryId}`, { includeSubCategories }],
    enabled: !!categoryId,
    queryFn: async () => {
      const params = includeSubCategories ? '?includeSubCategories=true' : '';
      const response = await apiRequest("GET", `/marketplace/service-categories/${categoryId}${params}`);
      return response.data || response;
    }
  });
}

export function useCreateServiceCategory() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (data: CreateServiceCategoryRequest) =>
      apiRequest("POST", "/marketplace/service-categories", data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/marketplace/service-categories"] });
      toast({
        title: "Category created",
        description: "Service category has been created successfully.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to create category. Please try again.",
        variant: "destructive",
      });
    },
  });
}

export function useUpdateServiceCategory() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateServiceCategoryRequest }) =>
      apiRequest("PUT", `/marketplace/service-categories/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/marketplace/service-categories"] });
      toast({
        title: "Category updated",
        description: "Service category has been updated successfully.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to update category. Please try again.",
        variant: "destructive",
      });
    },
  });
}

export function useDeleteServiceCategory() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (id: string) =>
      apiRequest("DELETE", `/marketplace/service-categories/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/marketplace/service-categories"] });
      toast({
        title: "Category deleted",
        description: "Service category has been deleted successfully.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to delete category. Please try again.",
        variant: "destructive",
      });
    },
  });
}

// Service Sub-Categories Hooks
export function useServiceSubCategories(categoryId: string) {
  return useQuery<ServiceSubCategory[]>({
    queryKey: [`/marketplace/service-categories/${categoryId}/subcategories`],
    queryFn: async () => {
      const response = await apiRequest("GET", `/marketplace/service-categories/${categoryId}/subcategories`);
      return response.data || response;
    },
    enabled: !!categoryId
  });
}

export function useCreateServiceSubCategory() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: ({ categoryId, data }: { categoryId: string; data: CreateServiceSubCategoryRequest }) =>
      apiRequest("POST", `/marketplace/service-categories/${categoryId}/subcategories`, data),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: [`/marketplace/service-categories/${variables.categoryId}/subcategories`] });
      queryClient.invalidateQueries({ queryKey: ["/marketplace/service-categories"] });
      toast({
        title: "Sub-category created",
        description: "Service sub-category has been created successfully.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to create sub-category. Please try again.",
        variant: "destructive",
      });
    },
  });
}

export function useUpdateServiceSubCategory() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: ({ categoryId, subcategoryId, data }: { categoryId: string; subcategoryId: string; data: UpdateServiceSubCategoryRequest }) =>
      apiRequest("PUT", `/marketplace/service-categories/${categoryId}/subcategories/${subcategoryId}`, data),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: [`/marketplace/service-categories/${variables.categoryId}/subcategories`] });
      queryClient.invalidateQueries({ queryKey: ["/marketplace/service-categories"] });
      toast({
        title: "Sub-category updated",
        description: "Service sub-category has been updated successfully.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to update sub-category. Please try again.",
        variant: "destructive",
      });
    },
  });
}

export function useDeleteServiceSubCategory() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: ({ categoryId, subcategoryId }: { categoryId: string; subcategoryId: string }) =>
      apiRequest("DELETE", `/marketplace/service-categories/${categoryId}/subcategories/${subcategoryId}`),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: [`/marketplace/service-categories/${variables.categoryId}/subcategories`] });
      queryClient.invalidateQueries({ queryKey: ["/marketplace/service-categories"] });
      toast({
        title: "Sub-category deleted",
        description: "Service sub-category has been deleted successfully.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to delete sub-category. Please try again.",
        variant: "destructive",
      });
    },
  });
}

// Service Types Hooks
export function useServiceTypes() {
  return useQuery<ServiceType[]>({
    queryKey: ["/marketplace/service-types"],
    queryFn: async () => {
      return await apiRequest("GET", "/marketplace/service-types");
    }
  });
}

export function useCreateServiceType() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (data: CreateServiceTypeRequest) =>
      apiRequest("POST", "/marketplace/service-types", data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/marketplace/service-types"] });
      toast({
        title: "Service type created",
        description: "Service type has been created successfully.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to create service type. Please try again.",
        variant: "destructive",
      });
    },
  });
}

export function useUpdateServiceType() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateServiceTypeRequest }) =>
      apiRequest("PUT", `/marketplace/service-types/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/marketplace/service-types"] });
      toast({
        title: "Service type updated",
        description: "Service type has been updated successfully.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to update service type. Please try again.",
        variant: "destructive",
      });
    },
  });
}

export function useDeleteServiceType() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (id: string) =>
      apiRequest("DELETE", `/marketplace/service-types/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/marketplace/service-types"] });
      toast({
        title: "Service type deleted",
        description: "Service type has been deleted successfully.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to delete service type. Please try again.",
        variant: "destructive",
      });
    },
  });
}